import { useHistory } from 'react-router-dom';
import { EventSettingsContextType, useEventSettings } from '../../../../lib/context-providers/event-settings-context/event-settings-context';
import { usePathPrefix } from '../../../../hooks/use-path-prefix';
import { normalizeRoutePath, getBookAMeetingPaths } from '../../../../lib/helpers/urlHelper';
import { AGENDA_PATH, REPLAYS_PATH, SESSION_PATH } from '../../../../config/routes/paths';
import { getConfigByPath } from '../../../../hooks/use-navbar-items';
import { IAuthContext, useAuth } from '../../../../lib/context-providers/auth-context';
import { SESSION_MEETING_PATH_REGEX, AGENDA_MEETING_PATH_REGEX } from '../../../../lib/constants';

export interface UseNavigationLogicProps {
  agendaLabel?: string;
}

export interface UseNavigationLogicReturn {
  navigationLabel: string;
  backToList: () => void;
}

interface NavigationPaths {
  agendaPath: string;
  sessionsPath: string;
  replaysPath: string;
  bookAMeetingPaths: string[];
}

const inferPathFromUrl = (pathname: string, paths: NavigationPaths): string => {
  if (pathname.includes(SESSION_PATH)) {
    return paths.sessionsPath;
  }

  if (pathname.includes(REPLAYS_PATH)) {
    return paths.replaysPath;
  }

  const bookAMeetingPathFromUrl = paths.bookAMeetingPaths.find(path => pathname.includes(path));
  if (bookAMeetingPathFromUrl) {
    return bookAMeetingPathFromUrl;
  }

  return paths.agendaPath;
};

const getPathFromHistoryState = (state: unknown, paths: NavigationPaths): string | null => {
  if (state === paths.sessionsPath) {
    return paths.sessionsPath;
  }

  if (state === paths.replaysPath) {
    return paths.replaysPath;
  }

  if (typeof state === 'string' && paths.bookAMeetingPaths.includes(state)) {
    return state;
  }

  return null;
};

const isMeetingDetailsPage = (pathname: string): boolean => {
  return SESSION_MEETING_PATH_REGEX.test(pathname) || AGENDA_MEETING_PATH_REGEX.test(pathname);
};

/**
 * Determines the back navigation path based on history state and URL pattern.
 * Prioritizes URL pattern inference when it clearly indicates the source page,
 * but uses history state when it's valid and the URL pattern is ambiguous (e.g., /session/.../meeting/...).
 * 
 * @param historyState - The history state from React Router
 * @param pathname - The current URL pathname
 * @param paths - Available navigation paths
 * @returns The path to navigate back to
 */
const determineBackNavigationPath = (
  historyState: unknown,
  pathname: string,
  paths: NavigationPaths
): string => {
  // Only use history state when on a meeting details page
  // This prevents history state from persisting when navigating between list pages
  if (isMeetingDetailsPage(pathname)) {
    const pathFromState = getPathFromHistoryState(historyState, paths);
    const pathFromUrl = inferPathFromUrl(pathname, paths);
    
    // If URL pattern clearly indicates a specific source (agenda/replays), always use URL inference
    // This ensures consistency and prevents stale history state from affecting navigation
    if (pathname.includes('/agenda/')) {
      // URL clearly indicates agenda source - use URL inference
      return pathFromUrl;
    }
    
    if (pathname.includes('/replays/')) {
      // URL clearly indicates replays source - use URL inference
      return pathFromUrl;
    }
    
    // For /session/.../meeting/... URLs, use history state if available (could be from sessions or Book a Meeting)
    // Otherwise infer from URL (defaults to sessions)
    if (pathFromState) {
      return pathFromState;
    }
  }

  return inferPathFromUrl(pathname, paths);
};

export const useNavigationLogic = (props: UseNavigationLogicProps): UseNavigationLogicReturn => {
  const { agendaLabel } = props;
  const history = useHistory();
  const eventSettings = useEventSettings() as EventSettingsContextType;
  const pathPrefix = usePathPrefix();
  const auth = useAuth() as IAuthContext;

  const bookAMeetingPaths = eventSettings
    ? getBookAMeetingPaths(eventSettings, pathPrefix, auth.isAuthenticated)
    : [];

  const paths: NavigationPaths = {
    agendaPath: normalizeRoutePath(AGENDA_PATH, pathPrefix),
    sessionsPath: normalizeRoutePath(SESSION_PATH, pathPrefix),
    replaysPath: normalizeRoutePath(REPLAYS_PATH, pathPrefix),
    bookAMeetingPaths,
  };

  const backNavigationPath = determineBackNavigationPath(
    history.location.state,
    history.location.pathname,
    paths
  );

  // Get label for the back button from event settings
  const { label } = (
    backNavigationPath &&
    eventSettings &&
    getConfigByPath(backNavigationPath, eventSettings, pathPrefix)
  ) || { label: agendaLabel ?? 'Agenda' };

  const backToList = (): void => {
    history.push(backNavigationPath || paths.agendaPath);
  };

  return {
    navigationLabel: label,
    backToList,
  };
};
